import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UIStore, UIStoreState, ThemeMode } from '@mellify/shared';
import { localStorageWrapper } from '../lib/storage';

/**
 * Resolve the effective theme based on mode and system preference.
 */
function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  return mode;
}

/**
 * Apply the resolved theme to the document element.
 */
function applyThemeToDOM(resolvedTheme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Initial UI state.
 */
const initialState: UIStoreState = {
  theme: 'system',
  resolvedTheme: resolveTheme('system'),
  modals: {},
  commandPaletteOpen: false,
  searchFocused: false,
};

/**
 * UI Store
 *
 * Manages global UI state including theme mode, modal visibility,
 * command palette, and search focus state. Theme preference is persisted
 * to localStorage.
 *
 * @example
 * ```ts
 * const { theme, toggleTheme, openModal, closeModal } = useUIStore();
 *
 * // With selector
 * const isDark = useUIStore((state) => state.resolvedTheme === 'dark');
 * ```
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setTheme: (theme: ThemeMode) => {
        const resolvedTheme = resolveTheme(theme);
        applyThemeToDOM(resolvedTheme);
        set({ theme, resolvedTheme });
      },

      toggleTheme: () => {
        const current = get().theme;
        let next: ThemeMode;
        if (current === 'light') {
          next = 'dark';
        } else if (current === 'dark') {
          next = 'system';
        } else {
          // system -> light
          next = 'light';
        }
        const resolvedTheme = resolveTheme(next);
        applyThemeToDOM(resolvedTheme);
        set({ theme: next, resolvedTheme });
      },

      openModal: (modalId: string) => {
        set((state) => ({
          modals: { ...state.modals, [modalId]: true },
        }));
      },

      closeModal: (modalId: string) => {
        set((state) => ({
          modals: { ...state.modals, [modalId]: false },
        }));
      },

      toggleModal: (modalId: string) => {
        set((state) => ({
          modals: { ...state.modals, [modalId]: !state.modals[modalId] },
        }));
      },

      closeAllModals: () => {
        set({ modals: {} });
      },

      toggleCommandPalette: () => {
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen }));
      },

      setCommandPaletteOpen: (open: boolean) => {
        set({ commandPaletteOpen: open });
      },

      setSearchFocused: (focused: boolean) => {
        set({ searchFocused: focused });
      },
    }),
    {
      name: 'mellify-ui',
      storage: createJSONStorage(() => localStorageWrapper),
      partialize: (state) => ({
        theme: state.theme,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            // Resolve and apply theme on rehydration
            const resolvedTheme = resolveTheme(state.theme);
            state.resolvedTheme = resolvedTheme;
            applyThemeToDOM(resolvedTheme);
          }
        };
      },
    }
  )
);

/**
 * Listen for system theme changes when theme mode is 'system'.
 */
if (typeof window !== 'undefined' && window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const state = useUIStore.getState();
    if (state.theme === 'system') {
      const resolvedTheme = resolveTheme('system');
      applyThemeToDOM(resolvedTheme);
      useUIStore.setState({ resolvedTheme });
    }
  });
}